import { Cookie, X } from 'lucide-react';
import { useEffect, useState } from 'react';

/**
 * Bumped when what is stored changes. A new value asks everybody again, which
 * is the only honest way to handle "we now keep something else".
 */
// v2: a third cookie, `gsn_device`, binds a session to this browser (ADR-038).
const NOTICE_VERSION = 'v2';
const KEY = 'gsn.cookies';

export const cookieChoice = (): string | null => {
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    // Private mode, or storage disabled. Treat as undecided rather than
    // crashing the sign-in page over a banner.
    return null;
  }
};

/**
 * What this application keeps on your device, and why.
 *
 * Written to be true rather than to look like every other cookie banner. This
 * console sets three cookies and all are **strictly necessary**: `gsn_session`
 * is how the server knows who you are, `gsn_csrf` is what stops another site
 * acting as you, and `gsn_device` ties your session to this browser so a copy
 * of it is useless elsewhere. In production each carries the `__Host-` prefix.
 * There is no analytics, no advertising, and nothing shared with anybody.
 *
 * That is why there is no "Reject" button. A reject that silently keeps the
 * cookies anyway — which is what it would have to do, since sign-in cannot work
 * without them — is a lie told in a dialog, and worse than not asking. The
 * honest options are to understand and continue, or to not use the console.
 * Refusing is still possible: it is your browser's cookie settings, and the
 * notice says so.
 */
export function CookieNotice() {
  const [choice, setChoice] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [detail, setDetail] = useState(false);

  useEffect(() => {
    setChoice(cookieChoice());
    setReady(true);
  }, []);

  const accept = () => {
    try {
      window.localStorage.setItem(KEY, NOTICE_VERSION);
    } catch {
      // Nothing to do. The banner will reappear next visit, which is a smaller
      // problem than an unhandled exception on the sign-in screen.
    }
    setChoice(NOTICE_VERSION);
  };

  if (!ready || choice === NOTICE_VERSION) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4"
      role="region"
      aria-label="Cookie notice"
    >
      <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-card p-4 shadow-card-hover">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-50">
            <Cookie className="h-4 w-4 text-primary-600" aria-hidden="true" />
          </div>

          <div className="min-w-0 flex-1">
            <h2 className="font-heading text-base">Cookies on this console</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Three cookies, all needed to sign you in and keep your session safe. No analytics,
              no advertising, nothing shared with anyone.
            </p>

            {detail && (
              <dl className="mt-3 space-y-2 rounded-xl bg-muted/60 p-3 text-sm">
                <div>
                  <dt className="font-medium">
                    <code>gsn_session</code> — how we know it is you
                  </dt>
                  <dd className="text-muted-foreground">
                    Unreadable to any script, sent only to this site, and ends when you sign out,
                    after four hours unused, or at the end of the day (30 days if you asked to stay
                    signed in). Its value changes every few minutes, so a copy soon stops working.
                  </dd>
                </div>
                <div>
                  <dt className="font-medium">
                    <code>gsn_csrf</code> — stops another site acting as you
                  </dt>
                  <dd className="text-muted-foreground">
                    Useless on its own, and lasts exactly as long as your session.
                  </dd>
                </div>
                <div>
                  <dt className="font-medium">
                    <code>gsn_device</code> — ties your session to this browser
                  </dt>
                  <dd className="text-muted-foreground">
                    A random number, unreadable to any script. If your session turns up with a
                    different one, it is ended at once. Lasts as long as your session.
                  </dd>
                </div>
                <div>
                  <dt className="font-medium">On a technician&apos;s phone</dt>
                  <dd className="text-muted-foreground">
                    Photos and job updates taken with no signal are held on the device until they
                    upload, so a job in a basement is not lost.
                  </dd>
                </div>
                <p className="pt-1 text-xs text-muted-foreground">
                  There is no &ldquo;reject&rdquo; here because signing in genuinely cannot work
                  without these, and a button that pretends otherwise would be dishonest. You can
                  still block them in your browser settings — the console will simply not sign you
                  in.
                </p>
              </dl>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-2">
              {/*
                Navy, not orange, and this is the one place the rule needed
                thinking about.

                Jua allows a single orange element per screen. This notice is a
                banner rather than a modal — it does not block the page, and it
                appears over *every* screen — so an orange button here would sit
                beside whatever that page's real action is. On the sign-in page
                it meant two orange buttons, one of which was a cookie
                acknowledgement competing with "Sign in".

                A blocking modal is different: it owns the screen while it is
                open, so its confirm button is legitimately the one action. That
                is why the leaving-confirmation dialog keeps orange and this
                does not.
              */}
              <button type="button" className="btn-secondary" onClick={accept}>
                I understand
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setDetail((open) => !open)}
                aria-expanded={detail}
              >
                {detail ? 'Hide the detail' : 'What is stored?'}
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={accept}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted"
            aria-label="Close and accept"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
